<script setup>
import { ref, computed, unref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email, requiredIf, minLength } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import { isValidPassword } from 'shared/helpers/Validators';
import SuiteExtensionSelect from './SuiteExtensionSelect.vue';
import AgentConversationPermissions from './AgentConversationPermissions.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const isPasswordFocused = ref(false);
const suiteRamalId = ref(null);
const conversationPermissions = ref(['conversation_manage']);

const shouldSetPassword = computed(() => {
  const rawValue = window?.chatwootConfig?.skipEmailConfirmation;
  return (
    rawValue === true ||
    rawValue === 'true' ||
    rawValue === 1 ||
    rawValue === '1'
  );
});

const MIN_PASSWORD_LENGTH = 6;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/;

const passwordRequirements = computed(() => {
  const password = agentPassword.value || '';
  return [
    {
      id: 'length',
      met: password.length >= MIN_PASSWORD_LENGTH,
      label: t('REGISTER.PASSWORD.REQUIREMENTS_LENGTH', {
        min: MIN_PASSWORD_LENGTH,
      }),
    },
    {
      id: 'uppercase',
      met: /[A-Z]/.test(password),
      label: t('REGISTER.PASSWORD.REQUIREMENTS_UPPERCASE'),
    },
    {
      id: 'lowercase',
      met: /[a-z]/.test(password),
      label: t('REGISTER.PASSWORD.REQUIREMENTS_LOWERCASE'),
    },
    {
      id: 'number',
      met: /[0-9]/.test(password),
      label: t('REGISTER.PASSWORD.REQUIREMENTS_NUMBER'),
    },
    {
      id: 'special',
      met: SPECIAL_CHAR_REGEX.test(password),
      label: t('REGISTER.PASSWORD.REQUIREMENTS_SPECIAL'),
    },
  ];
});

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
  agentPassword: {
    requiredIf: requiredIf(shouldSetPassword),
    // requiredIf only accepts the condition; other rules must run separately and
    // short-circuit when the password block is not shown (skipEmailConfirmation off).
    isValidPassword: value =>
      !unref(shouldSetPassword) || isValidPassword(value),
    minLength: value =>
      !unref(shouldSetPassword) ||
      minLength(MIN_PASSWORD_LENGTH).$validator(value),
  },
  agentPasswordConfirmation: {
    requiredIf: requiredIf(shouldSetPassword),
    // Compare strings explicitly; sameAs() unwraps refs internally, but unref here
    // matches Vuelidate behaviour and avoids relying on the validator object shape.
    sameAsPassword: value =>
      !unref(shouldSetPassword) || unref(value) === unref(agentPassword),
  },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentPassword,
  agentPasswordConfirmation,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const showConversationPermissions = computed(
  () => selectedRoleId.value === 'agent'
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      agent: {
        name: agentName.value,
        email: agentEmail.value,
        suite_ramal_id: suiteRamalId.value,
      },
    };

    if (shouldSetPassword.value) {
      payload.agent.password = agentPassword.value;
      payload.agent.password_confirmation = agentPasswordConfirmation.value;
    }

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.agent.custom_role_id = selectedRole.value.id;
    } else {
      payload.agent.role = selectedRole.value.name;
      payload.agent.conversation_permissions = conversationPermissions.value;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div v-if="shouldSetPassword" class="w-full">
        <label :class="{ error: v$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
          <input
            v-model="agentPassword"
            type="password"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
            autocomplete="new-password"
            @focus="isPasswordFocused = true"
            @blur="
              isPasswordFocused = false;
              v$.agentPassword.$touch();
            "
            @input="v$.agentPassword.$touch"
          />
        </label>
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div
            v-if="isPasswordFocused"
            class="mt-2 w-full text-xs rounded-lg px-4 py-3 bg-white dark:bg-n-solid-3 shadow-lg outline outline-1 outline-n-weak"
          >
            <ul role="list" class="space-y-1.5">
              <li
                v-for="item in passwordRequirements"
                :key="item.id"
                class="flex gap-2 items-start w-full"
              >
                <span
                  class="flex-none flex-shrink-0 w-3 mt-0.5"
                  :class="item.met ? 'text-n-teal-10' : 'text-n-slate-10'"
                >
                  {{ item.met ? '✓' : '○' }}
                </span>
                <span :class="item.met ? 'text-n-slate-11' : 'text-n-slate-10'">
                  {{ item.label }}
                </span>
              </li>
            </ul>
          </div>
        </Transition>
      </div>

      <AgentConversationPermissions
        v-if="showConversationPermissions"
        v-model="conversationPermissions"
        participating-exclusive
      />

      <div v-if="shouldSetPassword" class="w-full">
        <label :class="{ error: v$.agentPasswordConfirmation.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.LABEL') }}
          <input
            v-model="agentPasswordConfirmation"
            type="password"
            :placeholder="
              $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
            "
            autocomplete="new-password"
            @input="v$.agentPasswordConfirmation.$touch"
          />
        </label>
      </div>

      <SuiteExtensionSelect v-model="suiteRamalId" />

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
